import React, { useMemo } from 'react';
import moment from 'utils/moment';
import { signal } from '@preact/signals-react';
import styled from 'styled-components';
import Modal from './Modal';
import { IRepeatAppointmentRes } from 'services/types/apt';
import { calendarDatePickerRef } from 'CalendarFilter/DatePicker';
import { calendarScrollTo } from 'Calendar';
import calcTo from 'Calendar/helper';

/** Dinh dang startTime ma BE tra ve cho ca created va skipped. */
const BE_TIME_FORMAT = 'MM/DD/YYYY HH:mm';

/** Ket qua repeat kem ten khach, de bang doc ra nhu danh sach lich chu khong phai danh sach ngay. */
export type IRepeatResult = IRepeatAppointmentRes & { customerName?: string };

/**
 * Ket qua cua lan repeat vua roi. Saga gan gia tri vao day; gan null de dong.
 */
export const repeatResultSignal = signal<IRepeatResult | null>(null);

export const closeRepeatResult = () => {
  repeatResultSignal.value = null;
};

const parse = (value: string) => {
  const parsed = moment(value, BE_TIME_FORMAT, true);
  return parsed.isValid() ? parsed : null;
};

/**
 * Bien mang gio thanh mot cau mo ta nhip lap, chi khi that su co nhip.
 *
 * Cac chu ky deu la boi so cua 7 ngay nen thu trong tuan va gio le ra luon giong nhau — nhung
 * "le ra" khong phai bang chung. Kiem tra that, neu khong dong nhat thi im lang con hon noi sai.
 */
const describeCadence = (dates: moment.Moment[]): string | null => {
  if (!dates.length) return null;

  const weekdays = new Set(dates.map(d => d.format('dddd')));
  const times = new Set(dates.map(d => d.format('h:mm A')));
  if (weekdays.size !== 1 || times.size !== 1) return null;

  return `Every ${dates[0].format('dddd')} at ${dates[0].format('h:mm A')}`;
};

/**
 * Doi ngay xong moi keo man hinh: luoi duoc dung lai theo ngay moi, keo cung nhip se bi ghi
 * de. DatePicker cho 500ms roi moi chot ngay, va Calendar co mot lan tu keo ve khung gio hien
 * tai khi doi ngay — phai xep sau ca hai.
 */
const SCROLL_AFTER_DATE_CHANGE_MS = 700;

/** Id cua hang 15 phut trong luoi, dat trong Calendar (vd 10:05 AM -> to_10-00-AM_time). */
const timeSlotId = (date: moment.Moment) => {
  const rounded = date.clone().minutes(Math.floor(date.minutes() / 15) * 15);
  return `to_${rounded.format('hh')}-${rounded.format('mm')}-${rounded.format('A')}_time`;
};

/**
 * Keo den truoc buoi hen 30 phut chu khong dung ngay buoi hen: hang tieu de nhan vien dinh
 * o tren, canh dung mep se lam mat nua tren cua lich. Lui 30 phut cung cho thay lich xung
 * quanh. Neu buoi hen sat gio mo cua thi khong co hang do, dung mep chinh no.
 */
const findScrollRow = (date: moment.Moment) => {
  const lead = date.clone().subtract(30, 'minutes');
  return document.getElementById(timeSlotId(lead)) || document.getElementById(timeSlotId(date));
};

/**
 * Dua lich ve dung ngay va keo man hinh toi dung gio cua buoi vua bam.
 *
 * Keo theo id cua hang gio chu khong tinh pixel: chieu cao hang doi theo muc zoom, tinh tay
 * se lech moi khi nguoi dung phong to hay thu nho lich.
 */
const goToOccurrence = (date: moment.Moment) => {
  closeRepeatResult();
  calendarDatePickerRef.current?.goToDate(date);
  setTimeout(() => {
    const row = findScrollRow(date);
    if (row) {
      row.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
      return;
    }
    // Luoi chua kip dung lai thi lui ve cach tinh pixel, van hon la khong keo gi.
    calendarScrollTo.value(calcTo(date.format('MM-DD-YYYY HH:mm:ss')));
  }, SCROLL_AFTER_DATE_CHANGE_MS);
};

const RepeatResultModal = () => {
  const result = repeatResultSignal.value;

  const created = useMemo(
    () => (result?.created || []).map(o => parse(o.startTime)).filter(Boolean) as moment.Moment[],
    [result],
  );
  const cadence = useMemo(() => describeCadence(created), [created]);

  const skipped = result?.skipped || [];
  const createdCount = result?.created?.length || 0;

  // Gio chi nhac mot lan. Khi ca chuoi cung mot gio va dong nhip da noi ro gio do, lap lai
  // "9:45 AM" o tung buoi bi bo chi lam dong chu dai ra va kho doc.
  const skippedDates = useMemo(
    () => skipped.map(o => parse(o.startTime)),
    [skipped],
  );
  const showSkippedTime = useMemo(() => {
    if (!cadence) return true;
    const times = new Set(
      [...created, ...skippedDates.filter(Boolean) as moment.Moment[]].map(d => d.format('h:mm A')),
    );
    return times.size !== 1;
  }, [cadence, created, skippedDates]);

  return (
    <Modal
      footerStyle={{ display: 'none' }}
      title='REPEAT RESULT'
      width='auto'
      height='auto'
      open={!!result}
      onCancel={closeRepeatResult}
    >
      <ContentStyled>
        <p className='rr-count'>
          {createdCount} appointment{createdCount === 1 ? '' : 's'} added
        </p>
        {result?.customerName && <p className='rr-customer'>{result.customerName}</p>}
        {cadence && <p className='rr-cadence'>{cadence}</p>}

        {!!created.length && (
          <>
            <p className='rr-hint'>Tap a date to open it on the calendar.</p>
            <ul className='rr-chips'>
              {created.map((date, index) => (
                <li key={result?.created?.[index]?.appointmentId ?? index}>
                  <button
                    type='button'
                    className='rr-chip'
                    onClick={() => goToOccurrence(date)}
                  >
                    <span className='rr-chip-date'>{date.format('ddd, MMM D')}</span>
                    <span className='rr-chip-time'>{date.format('h:mm A')}</span>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}

        {!!skipped.length && (
          <div className='rr-skipped'>
            <p className='rr-skipped-title'>
              {skipped.length} skipped, the staff was already booked
            </p>
            <p className='rr-skipped-dates'>
              {skipped
                .map((o, index) => {
                  const parsed = skippedDates[index];
                  if (!parsed) return o.startTime;
                  return parsed.format(showSkippedTime ? 'MMM D, h:mm A' : 'MMM D');
                })
                .join(' · ')}
            </p>
            <p className='rr-skipped-hint'>Book these by hand if the customer still wants them.</p>
          </div>
        )}

        <button type='button' className='rr-done' onClick={closeRepeatResult}>Done</button>
      </ContentStyled>
    </Modal>
  );
};

export default RepeatResultModal;

const ContentStyled = styled.div`
  width: min(520px, calc(100vw - 48px));
  padding: 4px;
  font-family: Poppins;

  /* Nhan la dong nho mau xam; ten khach la dong lon mau dam. Tach hai vai tro ra hai dong
     de khong con doc lien thanh mot cau "added for khljl". */
  .rr-count {
    margin: 0;
    font-size: 16px;
    font-weight: 500;
    line-height: 24px;
    color: #64748B;
    font-variant-numeric: tabular-nums;
  }

  .rr-customer {
    margin: 2px 0 0;
    font-size: 28px;
    font-weight: 600;
    line-height: 36px;
    color: #1D2129;
    overflow-wrap: anywhere;
  }

  .rr-cadence {
    margin: 4px 0 0;
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    color: #64748B;
  }

  .rr-hint {
    margin: 20px 0 0;
    font-size: 13px;
    font-weight: 400;
    line-height: 20px;
    color: #64748B;
  }

  /* Cao toi da khoang 4 hang chip roi cuon: 12 buoi khong duoc day nut Done ra khoi man hinh. */
  .rr-chips {
    list-style: none;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin: 8px 0 0;
    padding: 4px;
    max-height: 288px;
    overflow-y: auto;
  }

  .rr-chip {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    min-height: 56px;
    padding: 8px 12px;
    border: 1px solid #C5DDF8;
    border-radius: 2px;
    background: #E8F3FF;
    cursor: pointer;
    text-align: left;
    transition: background-color 120ms cubic-bezier(0.25, 1, 0.5, 1),
      border-color 120ms cubic-bezier(0.25, 1, 0.5, 1),
      transform 120ms cubic-bezier(0.25, 1, 0.5, 1);
  }

  .rr-chip:hover {
    background: #D4E9FF;
    border-color: #0085E7;
    transform: translateY(-1px);
  }

  .rr-chip:active {
    background: #C5DDF8;
    transform: translateY(1px);
  }

  .rr-chip:focus-visible {
    outline: 2px solid #0085E7;
    outline-offset: 2px;
  }

  .rr-chip-date {
    font-family: Poppins;
    font-size: 16px;
    font-weight: 600;
    line-height: 22px;
    color: #1D2129;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }

  .rr-chip-time {
    font-family: Poppins;
    font-size: 13px;
    font-weight: 400;
    line-height: 18px;
    color: #4A5567;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }

  .rr-skipped {
    margin-top: 24px;
    padding: 12px 16px;
    border-radius: 2px;
    background: #FFEFEB;
  }

  .rr-skipped-title {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
    color: #C4400F;
  }

  .rr-skipped-dates {
    margin: 4px 0 0;
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    color: #1D2129;
    font-variant-numeric: tabular-nums;
  }

  .rr-skipped-hint {
    margin: 8px 0 0;
    font-size: 13px;
    font-weight: 400;
    line-height: 20px;
    color: #64748B;
  }

  .rr-done {
    margin-top: 24px;
    width: 100%;
    height: 48px;
    border: 0;
    border-radius: 2px;
    background: #232F3E;
    color: #FFF;
    font-family: Poppins;
    font-size: 18px;
    font-weight: 600;
    line-height: 24px;
    cursor: pointer;
    transition: background-color 120ms cubic-bezier(0.25, 1, 0.5, 1),
      transform 120ms cubic-bezier(0.25, 1, 0.5, 1);
  }

  .rr-done:hover {
    background: #33414F;
  }

  .rr-done:active {
    transform: translateY(1px);
  }

  .rr-done:focus-visible {
    outline: 2px solid #0085E7;
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    .rr-done,
    .rr-chip {
      transition: none;
    }

    .rr-done:active,
    .rr-chip:hover,
    .rr-chip:active {
      transform: none;
    }
  }
`;
